"use client";
import { Article } from "@/types";
import Image from "next/image";
import Link from "next/link";
import { memo } from "react";

type Props = {
  articles: Article[];
  maxOtherArticles?: number;
  showAuthor?: boolean;
  className?: string;
};

// Skeleton loader component for reusability
const SkeletonLoader = memo(() => (
  <section className="mx-auto max-w-3xl p-4">
    <h2 className="mb-4 text-xl font-bold uppercase text-gray-800 dark:text-white">
      Dernières Publications
    </h2>
    {/* Featured article skeleton */}
    <div className="mb-6 h-64 animate-pulse overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800" />
    {/* Other articles skeletons */}
    <div className="space-y-4">
      {Array.from({ length: 3 }, (_, idx) => (
        <div
          key={idx}
          className="flex animate-pulse items-center rounded-lg bg-gray-100 p-4 shadow-sm dark:bg-gray-800"
        >
          <div className="mr-4 h-20 w-24 flex-shrink-0 rounded-lg bg-gray-200 dark:bg-gray-700" />
          <div className="flex-grow space-y-2">
            <div className="h-4 rounded-md bg-gray-200 dark:bg-gray-700" />
            <div className="h-3 w-3/4 rounded-md bg-gray-200 dark:bg-gray-700" />
          </div>
        </div>
      ))}
    </div>
  </section>
));

SkeletonLoader.displayName = "SkeletonLoader";

// Featured article component
const FeaturedArticleCard = memo(({ article }: { article: Article }) => (
  <article className="overflow-hidden rounded-lg bg-white shadow-md transition-all duration-300 hover:scale-[1.01] hover:shadow-lg dark:bg-gray-800">
    <Link
      href={`/${article.slug}`}
      className="block focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      aria-label={`Lire l'article : ${article.title}`}
    >
      <div className="relative overflow-hidden">
        <Image
          src={article.cover_url || "/placeholder.jpg"}
          alt={`Image de l'article : ${article.title}`}
          width={800}
          height={450}
          className="h-64 w-full object-cover transition-transform duration-500 hover:scale-110"
          priority
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = "/placeholder.jpg";
          }}
        />
      </div>
    </Link>
    <div className="p-4">
      <h3 className="text-lg font-bold text-gray-800 transition-colors hover:text-blue-600 dark:text-white dark:hover:text-blue-400">
        <Link
          href={`/${article.slug}`}
          className="focus:underline focus:outline-none"
        >
          <span dangerouslySetInnerHTML={{ __html: article.title }} />
        </Link>
      </h3>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
        {article.authors && article.authors.length > 0 && (
          <>
            <span>par</span>
            <span className="font-medium">
              {article.authors.map((author) => author.name).join(", ")}
            </span>
            <span>•</span>
          </>
        )}
        <time dateTime={article.published_date}>{article.published_date}</time>
      </div>
      {article.excerpt && (
        <p
          className="mt-3 line-clamp-2 text-sm text-gray-600 dark:text-gray-400"
          dangerouslySetInnerHTML={{ __html: article.excerpt }}
        />
      )}
    </div>
  </article>
));

FeaturedArticleCard.displayName = "FeaturedArticleCard";

// Other article component
const ArticleCard = memo(({ article }: { article: Article }) => (
  <article className="group flex items-center rounded-lg bg-white shadow-md transition-all duration-300 hover:shadow-lg dark:bg-gray-800">
    <Link
      href={`/${article.slug}`}
      className="flex-shrink-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      aria-label={`Lire l'article : ${article.title}`}
    >
      <Image
        src={article.cover_url || "/placeholder.jpg"}
        alt={`Image de l'article : ${article.title}`}
        width={120}
        height={90}
        className="h-[90px] w-[120px] rounded-lg object-cover transition-transform duration-300 group-hover:scale-105"
        loading="lazy"
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.src = "/placeholder.jpg";
        }}
      />
    </Link>

    <div className="ml-4 flex min-w-0 flex-grow flex-col justify-center p-2">
      <h3 className="text-md line-clamp-2 font-semibold text-gray-800 transition-colors duration-300 group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400">
        <Link
          href={`/${article.slug}`}
          className="focus:underline focus:outline-none"
        >
          <span dangerouslySetInnerHTML={{ __html: article.title }} />
        </Link>
      </h3>

      <div className="mt-1 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
        <time dateTime={article.published_date}>{article.published_date}</time>
        {article.authors && article.authors.length > 0 && (
          <>
            <span>•</span>
            <span className="truncate">
              {article.authors[0].name}
              {article.authors.length > 1 && ` +${article.authors.length - 1}`}
            </span>
          </>
        )}
      </div>
    </div>
  </article>
));

ArticleCard.displayName = "ArticleCard";

const FeaturedArticles: React.FC<Props> = ({
  articles,
  maxOtherArticles = 5,
  showAuthor = true,
  className = "",
}) => {
  // Check if there are articles to display
  if (!articles || articles.length === 0) {
    return <SkeletonLoader />;
  }

  // Separate the featured article from the rest
  const [featuredArticle, ...otherArticles] = articles;
  const displayedOtherArticles = otherArticles.slice(0, maxOtherArticles);

  return (
    <section className={`mx-auto max-w-3xl p-4 ${className}`}>
      {/* Title */}
      <header className="mb-4">
        <h2 className="text-xl font-bold uppercase text-gray-800 dark:text-white">
          Dernières Publications
        </h2>
      </header>

      {/* Featured Article */}
      <FeaturedArticleCard article={featuredArticle} />

      {/* Other Articles */}
      {displayedOtherArticles.length > 0 && (
        <div className="mt-6 space-y-4">
          {displayedOtherArticles.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      )}
    </section>
  );
};

export default memo(FeaturedArticles);
